// deps
import cx from "classnames";
import React, { FunctionComponent, ReactElement, memo, useMemo } from "react";

// internal-deps
import { DeliveryAddress } from "@swiggy-private/payment-types";
import {
    getAddressAnnotation,
    getAddressInfoFromDeliveryAddress,
    getDeliveryAddressIcon,
} from "@swiggy-private/core-ui";
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";

// styles
import styles from "./DeliveryAddressText.scss";

//helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { hasInvalidAddressCategory } from "@payment-ui/components/tick-screen/helpers";
import { TICK_SCREEN_TEST_IDS } from "@swiggy-private/payment-test-ids";

interface DeliveryAddressTextProps {
    deliveryAddress: DeliveryAddress;
    className?: string;
}

function Icon({ deliveryAddress }: { deliveryAddress: DeliveryAddress }): ReactElement {
    const category = useMemo(() => {
        return hasInvalidAddressCategory(deliveryAddress)
            ? deliveryAddress.addressCategory
            : deliveryAddress?.userIntentAddressCategory;
    }, [deliveryAddress]);

    const IconElement = getDeliveryAddressIcon(category) as FunctionComponent<{
        className: string;
    }>;

    return <IconElement className={styles.annotationIcon} />;
}

function DeliveryAddressText({ deliveryAddress, className }: DeliveryAddressTextProps): ReactElement | null {
    const category = useMemo(() => {
        return hasInvalidAddressCategory(deliveryAddress)
            ? deliveryAddress.addressCategory
            : deliveryAddress?.userIntentAddressCategory;
    }, [deliveryAddress]);

    const annotation = useMemo(() => {
        if (hasInvalidAddressCategory(deliveryAddress)) {
            return getAddressAnnotation(deliveryAddress, category);
        }
        return deliveryAddress?.annotation || getAddressAnnotation(deliveryAddress, category);
    }, [deliveryAddress, category]);

    const addressInfo = useMemo(() => getAddressInfoFromDeliveryAddress(deliveryAddress), [deliveryAddress]);

    if (isEmpty(addressInfo)) {
        return null;
    }

    return (
        <Text variant="V2.body2.neutral.medium" color="text_Low_Emphasis" maxLines={2} textAlign="center">
            <div className={cx(styles.wrapper, className)} data-testid={TICK_SCREEN_TEST_IDS.ADDRESS_TEXT}>
                <span className={styles.annotation}>
                    <span data-testid={`${TICK_SCREEN_TEST_IDS.ADDRESS_ICON}_${category}`}>
                        <Icon deliveryAddress={deliveryAddress} />
                    </span>
                    <span data-testid={TICK_SCREEN_TEST_IDS.ADDRESS_ANNOTATION}>{`${annotation}: `}</span>
                </span>
                <span>{addressInfo}</span>
            </div>
        </Text>
    );
}

export default memo(DeliveryAddressText);
